import { useState, type ReactNode } from 'react'
import { EmptyState, InlineError, Skeleton } from '../../components/ui'
import { cn } from '../../utils/cn'
import { ServiceCard } from './ServiceCard'
import { ServiceRow } from './ServiceRow'
import { ServiceSheet } from './ServiceSheet'
import type { Service } from '../../types/storefront'

export interface ServiceCollectionProps {
  services: Service[]
  isLoading: boolean
  error?: unknown
  onRetry?: () => void
  /** "grid" for the home teaser, "list" for the services menu. */
  variant?: 'grid' | 'list'
  /** List variant only: breaks the rows under a display heading per category,
   *  the way the Stitch services menu reads. */
  grouped?: boolean
  emptyTitle?: string
  emptyBody?: ReactNode
  emptyAction?: ReactNode
  skeletonCount?: number
  className?: string
}

/**
 * Owns the detail sheet so every caller gets the same open/close behaviour and
 * only one sheet can be mounted per list.
 */
export function ServiceCollection({
  services,
  isLoading,
  error,
  onRetry,
  variant = 'grid',
  grouped = false,
  emptyTitle = 'No services yet',
  emptyBody = 'Check back soon, or call the salon and we will help you book.',
  emptyAction,
  skeletonCount = 3,
  className,
}: ServiceCollectionProps) {
  const [active, setActive] = useState<Service | null>(null)

  if (error) return <InlineError error={error} onRetry={onRetry} className={className} />

  if (isLoading) {
    return (
      <div className={cn(variant === 'grid' ? GRID : LIST, className)}>
        {Array.from({ length: skeletonCount }, (_, i) => (
          <div key={i} className="rounded-xl border border-primary/5 bg-surface-container-low p-6">
            {variant === 'grid' && <Skeleton shape="rect" className="mb-6 aspect-[4/5] w-full" />}
            <Skeleton lines={3} />
          </div>
        ))}
      </div>
    )
  }

  if (!services.length) {
    return <EmptyState icon="spa" title={emptyTitle} body={emptyBody} action={emptyAction} className={className} />
  }

  if (variant === 'list' && grouped) {
    return (
      <>
        <div className={cn('flex flex-col gap-12', className)}>
          {groupByCategory(services).map(([category, items]) => (
            <section key={category}>
              <h2 className="mb-6 border-b border-primary/10 pb-4 font-display text-headline-md text-on-surface">
                {category}
              </h2>
              <div className={LIST}>
                {items.map((service) => (
                  <ServiceRow key={service.id} service={service} onOpen={setActive} />
                ))}
              </div>
            </section>
          ))}
        </div>
        <ServiceSheet service={active} onClose={() => setActive(null)} />
      </>
    )
  }

  return (
    <>
      <div className={cn(variant === 'grid' ? GRID : LIST, className)}>
        {services.map((service) =>
          variant === 'grid' ? (
            <ServiceCard key={service.id} service={service} onOpen={setActive} />
          ) : (
            <ServiceRow key={service.id} service={service} onOpen={setActive} />
          ),
        )}
      </div>
      <ServiceSheet service={active} onClose={() => setActive(null)} />
    </>
  )
}

/** Preserves the order the API returned: the first time a category is seen is
 *  where its block goes. */
function groupByCategory(services: Service[]): [string, Service[]][] {
  const groups = new Map<string, Service[]>()
  for (const service of services) {
    const key = service.category_name ?? 'Other treatments'
    const bucket = groups.get(key)
    if (bucket) bucket.push(service)
    else groups.set(key, [service])
  }
  return [...groups.entries()]
}

const GRID = 'grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3'
const LIST = 'flex flex-col gap-6'
